import React, { useMemo, useState } from 'react';

import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatDateShort } from '~/services/TimeService';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';

import CampaignScheduleBrandRowsTable from './CampaignScheduleBrandRowsTable';
import { CampaignScheduleDropdownRow, CampaignScheduleSummaryRow } from './CampaignScheduleExpandableRow';
import CampaignScheduleHistoryCard from './CampaignScheduleHistoryCard';
import CampaignScheduleValuePillCell from './CampaignScheduleValuePillCell';
import ImportedSalesforceDatesSection from './ImportedSalesforceDatesSection';
import ScheduleDoctor from './ScheduleDoctor';
import { buildDependencyWarnings } from './campaignScheduleImportCandidates';
import { getMatchingBrandSchedulesByParentRow, getUnreconciledBrandSchedules } from './campaignScheduleMatching';
import {
  CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT,
  UNMATCHED_ROW_ID,
  toggleExpandedRow,
} from './campaignScheduleTabLayout';
import type { CampaignSchedulesTabProps } from './types';
import { useCampaignScheduleImportCandidates } from './useCampaignScheduleImportCandidates';

export default function CampaignSchedulesTab({
  brandSchedules,
  error,
  isLoading,
  offerId,
  onImportFromSalesforce,
  preview,
}: CampaignSchedulesTabProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { parentRows, candidates, candidateWarnings } = useCampaignScheduleImportCandidates(preview, brandSchedules);
  useDealInformationTabCount('campaign-schedules', isLoading || error ? undefined : parentRows.length);
  const matchingByParentRowId = useMemo(
    () => getMatchingBrandSchedulesByParentRow(parentRows, brandSchedules),
    [parentRows, brandSchedules],
  );
  const unmatchedBrandSchedules = useMemo(
    () => getUnreconciledBrandSchedules(matchingByParentRowId, brandSchedules),
    [matchingByParentRowId, brandSchedules],
  );

  // Reflects the same real import blockers the modal shows (e.g. dates falling outside an existing
  // schedule), not just the derivation-time warnings from `preview`.
  const dependencyWarnings = useMemo(() => buildDependencyWarnings(candidates), [candidates]);
  const scheduleDoctorWarnings = useMemo(
    () => [...(preview?.warnings ?? []), ...candidateWarnings, ...dependencyWarnings],
    [preview, candidateWarnings, dependencyWarnings],
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading campaign schedules.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load campaign schedules.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Typography fontWeight={700} variant="h2">
          Campaign Schedules
        </Typography>
        <Button
          disabled={!offerId}
          onClick={onImportFromSalesforce}
          startIcon={<CloudDownloadIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          variant="contained"
        >
          Import from Salesforce
        </Button>
      </Stack>

      <ImportedSalesforceDatesSection brandSchedules={brandSchedules} />

      <ScheduleDoctor warnings={scheduleDoctorWarnings} />

      <Paper variant="outlined">
        <Typography color="text.secondary" sx={{ px: 3, pt: 3, pb: 1.5 }} variant="body2">
          Expand a Salesforce campaign schedule to see the matching offer schedules with a matching type, brand, region,
          and date range. Importing from Salesforce opens a preview of the schedules derived from the linked Salesforce
          Campaign Schedule records before applying them to this offer.
        </Typography>
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                    color: 'info.dark',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: 'uppercase',
                  },
                }}
              >
                <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}>Salesforce Campaign Schedules</TableCell>
              </TableRow>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                    color: 'info.dark',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: 'uppercase',
                  },
                }}
              >
                <TableCell />
                <TableCell>Type</TableCell>
                <TableCell>Start Offline</TableCell>
                <TableCell>End Offline</TableCell>
                <TableCell>Start Online</TableCell>
                <TableCell>End Online</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Region</TableCell>
                <TableCell align="center">Matching schedule rows</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parentRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}>
                    <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                      No Salesforce campaign schedules found for this offer.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {parentRows.map((parentRow) => {
                const matchingSchedules = matchingByParentRowId.get(parentRow.id) ?? [];
                const combinationCount = parentRow.brands.length * parentRow.markets.length;
                const isExpanded = expandedRows.has(parentRow.id);

                return (
                  <React.Fragment key={parentRow.id}>
                    <CampaignScheduleSummaryRow
                      isExpanded={isExpanded}
                      onToggle={() => setExpandedRows((current) => toggleExpandedRow(current, parentRow.id))}
                    >
                      <TableCell>
                        <CampaignScheduleValuePillCell label="types" values={parentRow.campaignTypes} />
                      </TableCell>
                      <TableCell>{parentRow.startOffline ? formatDateShort(parentRow.startOffline) : '—'}</TableCell>
                      <TableCell>{parentRow.endOffline ? formatDateShort(parentRow.endOffline) : '—'}</TableCell>
                      <TableCell>{parentRow.startOnline ? formatDateShort(parentRow.startOnline) : '—'}</TableCell>
                      <TableCell>{parentRow.endOnline ? formatDateShort(parentRow.endOnline) : '—'}</TableCell>
                      <TableCell>
                        <CampaignScheduleValuePillCell label="brands" values={parentRow.brands} />
                      </TableCell>
                      <TableCell>
                        <CampaignScheduleValuePillCell label="regions" values={parentRow.markets} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${matchingSchedules.length} / ${combinationCount}`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </CampaignScheduleSummaryRow>
                    <CampaignScheduleDropdownRow
                      colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}
                      isExpanded={isExpanded}
                    >
                      <CampaignScheduleBrandRowsTable
                        emptyMessage="No matching campaign schedules associated with the offer"
                        schedules={matchingSchedules}
                      />
                    </CampaignScheduleDropdownRow>
                  </React.Fragment>
                );
              })}
              <CampaignScheduleSummaryRow
                isExpanded={expandedRows.has(UNMATCHED_ROW_ID)}
                sx={{ '& .MuiTableCell-root': { bgcolor: 'grey.50' } }}
                onToggle={() => setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID))}
              >
                <TableCell colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT - 2}>
                  <Typography fontWeight={700} variant="body2">
                    Schedule Rows with No Matching Salesforce Campaign Schedule
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label={unmatchedBrandSchedules.length} size="small" variant="outlined" />
                </TableCell>
              </CampaignScheduleSummaryRow>
              <CampaignScheduleDropdownRow
                colSpan={CAMPAIGN_SCHEDULES_TABLE_COLUMN_COUNT}
                isExpanded={expandedRows.has(UNMATCHED_ROW_ID)}
              >
                <CampaignScheduleBrandRowsTable
                  emptyMessage="No schedule rows without a matching Salesforce campaign schedule."
                  schedules={unmatchedBrandSchedules}
                />
              </CampaignScheduleDropdownRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CampaignScheduleHistoryCard offerId={offerId} />
    </Stack>
  );
}
